import { useState, useMemo } from "react";

/**
 * Hook sắp xếp dùng chung cho mọi danh sách.
 * @param {Array} items - mảng dữ liệu gốc
 * @param {string} defaultKey - trường sắp xếp mặc định
 * @param {"asc"|"desc"} defaultDir - chiều mặc định
 * @returns {{ sorted, sortKey, sortDir, handleSort, SortIcon }}
 *
 * Cách dùng trong <th>:
 *   <th onClick={() => handleSort("name")} className="cursor-pointer select-none">
 *     Tên {SortIcon("name")}
 *   </th>
 */

// So sánh thông minh: "KH002" < "KH010", "BB26000006" < "BB26000010"
function naturalCompare(a, b) {
  const tokenize = (s) =>
    String(s).split(/(\d+)/).map((t) => (isNaN(t) ? t : parseInt(t, 10)));
  const ta = tokenize(a);
  const tb = tokenize(b);
  for (let i = 0; i < Math.max(ta.length, tb.length); i++) {
    const va = ta[i] ?? "";
    const vb = tb[i] ?? "";
    if (typeof va === "number" && typeof vb === "number") {
      if (va !== vb) return va - vb;
    } else {
      const cmp = String(va).localeCompare(String(vb), "vi", { sensitivity: "base" });
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

export function useSort(items, defaultKey = "", defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return items;
    return [...items].sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];

      // null/undefined xuống cuối
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;

      // So sánh số thuần
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }

      // So sánh ngày (ISO string)
      const da = Date.parse(va);
      const db = Date.parse(vb);
      if (!isNaN(da) && !isNaN(db)) {
        return sortDir === "asc" ? da - db : db - da;
      }

      // So sánh chuỗi alphanumeric (KH001, BB26000006...)
      const cmp = naturalCompare(va, vb);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [items, sortKey, sortDir]);

  // Component icon hiển thị trạng thái sort
  const SortIcon = (key) => {
    if (sortKey !== key)
      return <span className="ml-1 text-ink-muted/40 text-[10px]">⇅</span>;
    return (
      <span className="ml-1 text-bamboo text-[10px]">
        {sortDir === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  return { sorted, sortKey, sortDir, handleSort, SortIcon };
}